import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, doc, getDoc, setDoc, deleteDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import {
  ShoppingCart, Trash2, Plus, Minus, MapPin, CreditCard,
  Package, Check, AlertCircle, Search, Building2, Star, X, Clock
} from 'lucide-react';

function Cart({ user, userInfo }) {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [userData, setUserData] = useState(null);
  
  // 배송지 정보
  const [shipping, setShipping] = useState({
    name: '',
    phone: '',
    zipcode: '',
    address: '',
    detailAddress: '',
    memo: ''
  });
  
  // 결제 방법
  const [paymentMethod, setPaymentMethod] = useState('bank');

  // 무통장입금 관련
  const [depositorName, setDepositorName] = useState('');
  const [sellerBankInfoMap, setSellerBankInfoMap] = useState({});
  
  // 저장된 배송지 목록
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [editingAddressIndex, setEditingAddressIndex] = useState(null);
  
  // 주소 검색 모달
  const [showPostcodeModal, setShowPostcodeModal] = useState(false);
  const postcodeRef = useRef(null);

  // 약관 동의
  const [agreeTerms, setAgreeTerms] = useState(false);

  // Firestore 장바구니 사용자 ID (보안 규칙상 auth.uid 사용)
  const getCartUserId = () => {
    console.log('🛒 getCartUserId called - user:', user?.uid, 'userInfo:', userInfo?.uid);
    if (!user?.uid) return null;
    return user.uid;
  };

  // 사용자 데이터 로드
  useEffect(() => {
    const loadUserData = async () => {
      if (!user?.uid) {
        setLoading(false);
        return;
      }
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setUserData(data);
          setShipping(prev => ({
            ...prev,
            name: data.name || data.representativeName || '',
            phone: data.phone || data.businessPhone || ''
          }));
        }
      } catch (error) {
        console.error('사용자 데이터 로드 실패:', error);
      }
    };
    loadUserData();
  }, [user]);

  // 장바구니 로드 (Firestore)
  useEffect(() => {
    const loadCart = async () => {
      const userId = getCartUserId();
      console.log('🛒 장바구니 로드 시작 - userId:', userId);
      if (!userId) {
        console.log('🛒 userId 없음 - 빈 장바구니');
        setCartItems([]);
        setLoading(false);
        return;
      }
      try {
        const cartDoc = await getDoc(doc(db, 'userCarts', userId));
        console.log('🛒 Firestore 조회 결과 - exists:', cartDoc.exists(), 'data:', cartDoc.exists() ? cartDoc.data() : null);
        if (cartDoc.exists()) {
          const data = cartDoc.data();
          const items = (data.items || []).map(item => ({
            id: item.productId,
            name: item.productName,
            price: item.price || 0,
            quantity: item.quantity || 1,
            image: item.imageUrl || '',
            sellerId: item.sellerId || '',
            sellerName: item.sellerName || '',
            selectedOption: typeof item.selectedOptions === 'string' ? item.selectedOptions
              : typeof item.optionsText === 'string' ? item.optionsText
              : item.selectedOptions ? JSON.stringify(item.selectedOptions) : null,
            additionalPrice: item.additionalPrice || 0,
            addedAt: item.addedAt || null
          }));
          console.log('🛒 파싱된 아이템:', items.length, '개');
          setCartItems(items);
        } else {
          console.log('🛒 장바구니 문서 없음');
          setCartItems([]);
        }
      } catch (error) {
        console.error('🛒 장바구니 로드 실패:', error);
        setCartItems([]);
      }
      setLoading(false);
    };
    loadCart();
  }, [user]);

  // 저장된 배송지 로드 (Firestore + localStorage 마이그레이션)
  useEffect(() => {
    const loadAddresses = async () => {
      if (!user?.uid) return;
      try {
        const userDocSnap = await getDoc(doc(db, 'users', user.uid));
        let addresses = [];
        if (userDocSnap.exists()) {
          const data = userDocSnap.data();
          addresses = data.shippingAddresses || data.addresses || [];
        }

        // localStorage에 기존 배송지가 있으면 Firestore로 마이그레이션
        if (addresses.length === 0) {
          const localKey = `dentconnect_addresses_${user.uid}`;
          const localData = localStorage.getItem(localKey);
          if (localData) {
            try {
              const localAddresses = JSON.parse(localData);
              if (Array.isArray(localAddresses) && localAddresses.length > 0) {
                addresses = localAddresses;
                await updateDoc(doc(db, 'users', user.uid), { shippingAddresses: localAddresses });
                localStorage.removeItem(localKey);
                console.log('✅ 배송지 localStorage → Firestore 마이그레이션 완료');
              }
            } catch (parseErr) { console.error('localStorage 파싱 실패:', parseErr); }
          }
        }

        setSavedAddresses(addresses);
        const defaultAddr = addresses.find(a => a.isDefault);
        if (defaultAddr) {
          setShipping({
            name: defaultAddr.name,
            phone: defaultAddr.phone,
            zipcode: defaultAddr.zipcode || defaultAddr.zipCode || '',
            address: defaultAddr.address,
            detailAddress: defaultAddr.detailAddress || '',
            memo: defaultAddr.memo || ''
          });
        }
      } catch (e) { console.error('배송지 로드 실패:', e); }
    };
    loadAddresses();
  }, [user]);

  // 판매자별 계좌 정보 로드
  useEffect(() => {
    const loadSellerBankInfo = async () => {
      const sellerIds = [...new Set(cartItems.map(item => item.sellerId).filter(Boolean))];
      if (sellerIds.length === 0) return;

      const bankInfoMap = {};
      for (const sellerId of sellerIds) {
        try {
          const sellerDoc = await getDoc(doc(db, 'users', sellerId));
          if (sellerDoc.exists()) {
            const data = sellerDoc.data();
            bankInfoMap[sellerId] = {
              sellerName: data.storeName || data.businessName || '판매자',
              bankName: data.bankName || '',
              bankAccount: data.bankAccount || '',
              bankHolder: data.bankHolder || '',
            };
          }
        } catch (error) {
          console.error('판매자 계좌 정보 로드 실패:', sellerId, error);
        }
      }
      setSellerBankInfoMap(bankInfoMap);
    };
    if (cartItems.length > 0) {
      loadSellerBankInfo();
    }
  }, [cartItems]);

  // Firestore에 배송지 목록 저장
  const syncAddressesToFirestore = async (addresses) => {
    if (!user?.uid) return;
    try {
      await updateDoc(doc(db, 'users', user.uid), { shippingAddresses: addresses });
    } catch (e) { console.error('배송지 저장 실패:', e); }
  };

  // 배송지 저장
  const saveAddress = async () => {
    if (!shipping.name || !shipping.phone || !shipping.address) {
      alert('배송지 정보를 모두 입력해주세요.');
      return;
    }
    const newAddress = { ...shipping, id: Date.now(), isDefault: savedAddresses.length === 0 };
    let updated;
    if (editingAddressIndex !== null) {
      updated = [...savedAddresses];
      updated[editingAddressIndex] = { ...newAddress, id: savedAddresses[editingAddressIndex].id };
    } else {
      updated = [...savedAddresses, newAddress];
    }
    setSavedAddresses(updated);
    setEditingAddressIndex(null);
    await syncAddressesToFirestore(updated);
    alert(editingAddressIndex !== null ? '배송지가 수정되었습니다.' : '배송지가 저장되었습니다.');
  };

  // 배송지 선택
  const selectAddress = (addr) => {
    setShipping({
      name: addr.name,
      phone: addr.phone,
      zipcode: addr.zipcode || addr.zipCode || '',
      address: addr.address,
      detailAddress: addr.detailAddress || '',
      memo: addr.memo || ''
    });
    setShowAddressModal(false);
  };

  // 배송지 삭제
  const deleteAddress = async (index) => {
    if (!window.confirm('이 배송지를 삭제하시겠습니까?')) return;
    const updated = savedAddresses.filter((_, i) => i !== index);
    setSavedAddresses(updated);
    await syncAddressesToFirestore(updated);
  };

  // 기본 배송지 설정
  const setDefaultAddress = async (index) => {
    const updated = savedAddresses.map((addr, i) => ({ ...addr, isDefault: i === index }));
    setSavedAddresses(updated);
    selectAddress(updated[index]);
    await syncAddressesToFirestore(updated);
  };

  // 장바구니 저장 (Firestore)
  const saveCart = async (items) => {
    setCartItems(items);
    const userId = getCartUserId();
    if (!userId) return;
    try {
      const firestoreItems = items.map(item => ({
        productId: item.id,
        productName: item.name || item.productName || '',
        price: item.price || 0,
        quantity: item.quantity || 1,
        imageUrl: item.image || item.imageUrl || '',
        sellerId: item.sellerId || '',
        sellerName: item.sellerName || item.seller || '',
        selectedOptions: typeof item.selectedOption === 'string' ? item.selectedOption : null,
        optionsText: typeof item.selectedOption === 'string' ? item.selectedOption : '',
        additionalPrice: item.additionalPrice || 0,
        addedAt: item.addedAt || Timestamp.now()
      }));
      await setDoc(doc(db, 'userCarts', userId), {
        items: firestoreItems,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('장바구니 저장 실패:', error);
    }
  };

  const updateQuantity = (productId, newQty) => {
    if (newQty < 1) return;
    const newCart = cartItems.map(item => 
      item.id === productId ? { ...item, quantity: newQty } : item
    );
    saveCart(newCart);
  };

  const removeItem = (productId) => {
    if (!window.confirm('장바구니에서 삭제하시겠습니까?')) return;
    const newCart = cartItems.filter(item => item.id !== productId);
    saveCart(newCart);
  };

  // 다음 주소 검색 API - 모달로 열기
  const openAddressSearch = () => {
    setShowPostcodeModal(true);
  };

  // 다음 우편번호 스크립트 로드
  const loadDaumPostcode = () => {
    return new Promise((resolve) => {
      if (window.daum && window.daum.Postcode) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.src = '//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';
      script.onload = () => resolve();
      document.head.appendChild(script);
    });
  };

  // 주소 검색 모달이 열릴 때 다음 우편번호 서비스 embed
  useEffect(() => {
    if (showPostcodeModal && postcodeRef.current) {
      // 기존 내용 제거
      postcodeRef.current.innerHTML = '<p style="text-align:center;padding:40px;color:#94a3b8;">주소 검색 서비스를 불러오는 중...</p>';
      
      loadDaumPostcode().then(() => {
        if (postcodeRef.current) {
          postcodeRef.current.innerHTML = '';
          new window.daum.Postcode({
            oncomplete: (data) => {
              setShipping(prev => ({
                ...prev,
                zipcode: data.zonecode,
                address: data.roadAddress || data.jibunAddress
              }));
              setShowPostcodeModal(false);
            },
            width: '100%',
            height: '100%'
          }).embed(postcodeRef.current);
        }
      });
    }
  }, [showPostcodeModal]);

  // 금액 계산
  const subtotal = cartItems.reduce((sum, item) => sum + ((item.price || 0) * (item.quantity || 1)), 0);
  const shippingFee = subtotal >= 50000 ? 0 : 3000;
  const total = subtotal + shippingFee;

  // 결제 처리
  const handlePayment = async () => {
    if (!user?.uid) {
      alert('로그인이 필요합니다.');
      navigate('/login');
      return;
    }
    if (cartItems.length === 0) {
      alert('장바구니가 비어있습니다.');
      return;
    }
    if (!shipping.name || !shipping.phone || !shipping.address) {
      alert('배송지 정보를 모두 입력해주세요.');
      return;
    }
    if (!agreeTerms) {
      alert('구매 약관에 동의해주세요.');
      return;
    }

    if (paymentMethod === 'bank' && !depositorName.trim()) {
      alert('입금자명을 입력해주세요.');
      return;
    }

    setProcessing(true);
    try {
      // 판매자별로 주문 분리
      const sellerGroups = {};
      cartItems.forEach(item => {
        const sellerId = item.sellerId || 'unknown';
        if (!sellerGroups[sellerId]) {
          sellerGroups[sellerId] = {
            sellerId: sellerId,
            sellerName: item.sellerName || item.seller || '판매자',
            items: []
          };
        }
        sellerGroups[sellerId].items.push(item);
      });

      // 각 판매자별 주문 생성
      for (const sellerId in sellerGroups) {
        const group = sellerGroups[sellerId];
        const groupSubtotal = group.items.reduce((sum, item) => sum + ((item.price || 0) * (item.quantity || 1)), 0);
        const groupShippingFee = groupSubtotal >= 50000 ? 0 : 3000;

        // 판매자 계좌 정보 스냅샷
        const bankInfo = sellerBankInfoMap[group.sellerId] || {};

        const paymentData = {
          method: paymentMethod,
          subtotal: groupSubtotal,
          shippingFee: groupShippingFee,
          total: groupSubtotal + groupShippingFee,
        };

        if (paymentMethod === 'bank') {
          paymentData.depositorName = depositorName.trim();
          paymentData.sellerBankInfo = {
            bankName: bankInfo.bankName || '',
            bankAccount: bankInfo.bankAccount || '',
            bankHolder: bankInfo.bankHolder || '',
          };
        }

        await addDoc(collection(db, 'marketplaceOrders'), {
          buyerId: user.uid,
          buyerName: userData?.businessName || userData?.name || user.displayName || '구매자',
          sellerId: group.sellerId,
          sellerName: group.sellerName,
          items: group.items.map(item => ({
            productId: item.id,
            productName: item.name || item.productName,
            image: item.image || item.imageUrl || item.images?.[0] || '',
            price: item.price || 0,
            quantity: item.quantity || 1,
            options: typeof item.selectedOption === 'string' ? item.selectedOption : null
          })),
          shipping: {
            name: shipping.name,
            phone: shipping.phone,
            zipcode: shipping.zipcode,
            address: shipping.address,
            detailAddress: shipping.detailAddress,
            memo: shipping.memo
          },
          payment: paymentData,
          status: paymentMethod === 'bank' ? 'pending_payment' : 'paid',
          createdAt: new Date(),
          updatedAt: new Date()
        });
      }

      // 장바구니 비우기 (Firestore)
      const userId = getCartUserId();
      if (userId) {
        await deleteDoc(doc(db, 'userCarts', userId));
      }
      setCartItems([]);

      alert('주문이 완료되었습니다!');
      navigate('/my-orders');
    } catch (error) {
      console.error('주문 실패:', error);
      alert('주문 처리 중 오류가 발생했습니다.');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner} />
        <p>장바구니를 불러오는 중...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.content}>
        {/* 왼쪽: 장바구니 + 배송지 + 결제방법 */}
        <div style={styles.leftPanel}>
          {/* 장바구니 상품 */}
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <ShoppingCart size={20} color="#6366f1" />
              <h2 style={styles.sectionTitle}>장바구니</h2>
              <span style={styles.itemCount}>{cartItems.length}개</span>
            </div>
            
            {cartItems.length === 0 ? (
              <div style={styles.emptyState}>
                <Package size={48} color="#cbd5e1" />
                <p>장바구니가 비어있습니다</p>
                <button onClick={() => navigate('/marketplace')} style={styles.shopBtn}>
                  쇼핑하러 가기
                </button>
              </div>
            ) : (
              <div style={styles.cartList}>
                {cartItems.map(item => (
                  <div key={item.id} style={styles.cartItem}>
                    <img 
                      src={item.image || item.imageUrl || item.images?.[0] || '/placeholder-product.png'} 
                      alt={item.name || item.productName}
                      style={styles.itemImage}
                      onError={(e) => { e.target.src = '/placeholder-product.png'; }}
                    />
                    <div style={styles.itemInfo}>
                      <p style={styles.itemSeller}>{typeof item.sellerName === 'string' ? item.sellerName : (typeof item.seller === 'string' ? item.seller : '판매자')}</p>
                      <p style={styles.itemName}>{typeof item.name === 'string' ? item.name : (typeof item.productName === 'string' ? item.productName : '')}</p>
                      {item.selectedOption && typeof item.selectedOption === 'string' && (
                        <p style={styles.itemOption}>옵션: {item.selectedOption}</p>
                      )}
                      <p style={styles.itemPrice}>{(item.price || 0).toLocaleString()}원</p>
                    </div>
                    <div style={styles.itemActions}>
                      <div style={styles.quantityControl}>
                        <button 
                          onClick={() => updateQuantity(item.id, (item.quantity || 1) - 1)}
                          style={styles.qtyBtn}
                          disabled={(item.quantity || 1) <= 1}
                        >
                          <Minus size={14} />
                        </button>
                        <span style={styles.qtyValue}>{item.quantity || 1}</span>
                        <button 
                          onClick={() => updateQuantity(item.id, (item.quantity || 1) + 1)}
                          style={styles.qtyBtn}
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                      <p style={styles.itemTotal}>
                        {((item.price || 0) * (item.quantity || 1)).toLocaleString()}원
                      </p>
                      <button onClick={() => removeItem(item.id)} style={styles.deleteBtn}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 배송지 정보 */}
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <MapPin size={20} color="#6366f1" />
              <h2 style={styles.sectionTitle}>배송지 정보</h2>
              <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
                {savedAddresses.length > 0 && (
                  <button onClick={() => setShowAddressModal(true)} style={styles.addressBtn}>
                    <MapPin size={14} />
                    저장된 배송지 ({savedAddresses.length})
                  </button>
                )}
                <button onClick={saveAddress} style={styles.saveAddressBtn}>
                  <Plus size={14} />
                  현재 배송지 저장
                </button>
              </div>
            </div>
            
            <div style={styles.formGrid}>
              <div style={styles.formRow}>
                <label style={styles.label}>받는 분 *</label>
                <input
                  type="text"
                  value={shipping.name}
                  onChange={(e) => setShipping({ ...shipping, name: e.target.value })}
                  placeholder="이름"
                  style={styles.input}
                />
              </div>
              
              <div style={styles.formRow}>
                <label style={styles.label}>연락처 *</label>
                <input
                  type="tel"
                  value={shipping.phone}
                  onChange={(e) => setShipping({ ...shipping, phone: e.target.value })}
                  placeholder="010-0000-0000"
                  style={styles.input}
                />
              </div>
              
              <div style={styles.formRow}>
                <label style={styles.label}>주소 *</label>
                <div style={styles.addressRow}>
                  <input
                    type="text"
                    value={shipping.zipcode}
                    readOnly
                    placeholder="우편번호"
                    style={{ ...styles.input, width: 100, flex: 'none' }}
                  />
                  <button onClick={openAddressSearch} style={styles.searchBtn}>
                    <Search size={16} />
                    주소 검색
                  </button>
                </div>
                <input
                  type="text"
                  value={shipping.address}
                  readOnly
                  placeholder="기본 주소"
                  style={{ ...styles.input, marginTop: 8 }}
                />
                <input
                  type="text"
                  value={shipping.detailAddress}
                  onChange={(e) => setShipping({ ...shipping, detailAddress: e.target.value })}
                  placeholder="상세 주소 입력"
                  style={{ ...styles.input, marginTop: 8 }}
                />
              </div>
              
              <div style={styles.formRow}>
                <label style={styles.label}>배송 메모</label>
                <select
                  value={shipping.memo}
                  onChange={(e) => setShipping({ ...shipping, memo: e.target.value })}
                  style={styles.select}
                >
                  <option value="">배송 메모를 선택해주세요</option>
                  <option value="문 앞에 놓아주세요">문 앞에 놓아주세요</option>
                  <option value="경비실에 맡겨주세요">경비실에 맡겨주세요</option>
                  <option value="배송 전 연락 부탁드립니다">배송 전 연락 부탁드립니다</option>
                  <option value="부재 시 연락 부탁드립니다">부재 시 연락 부탁드립니다</option>
                </select>
              </div>
            </div>
          </div>

          {/* 결제 방법 */}
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <CreditCard size={20} color="#6366f1" />
              <h2 style={styles.sectionTitle}>결제 방법</h2>
            </div>
            
            <div style={styles.paymentMethods}>
              {[
                { id: 'bank', label: '무통장입금', icon: Building2, disabled: false },
                { id: 'card', label: '신용/체크카드', icon: CreditCard, disabled: true },
                { id: 'kakao', label: '카카오페이', icon: CreditCard, disabled: true }
              ].map(method => (
                <button
                  key={method.id}
                  onClick={() => !method.disabled && setPaymentMethod(method.id)}
                  style={{
                    ...styles.paymentBtn,
                    borderColor: paymentMethod === method.id ? '#6366f1' : '#e2e8f0',
                    backgroundColor: method.disabled ? '#f8fafc' : (paymentMethod === method.id ? '#eef2ff' : '#fff'),
                    opacity: method.disabled ? 0.6 : 1,
                    cursor: method.disabled ? 'not-allowed' : 'pointer',
                    position: 'relative',
                  }}
                >
                  <method.icon size={20} color={method.disabled ? '#94a3b8' : (paymentMethod === method.id ? '#6366f1' : '#64748b')} />
                  <span style={{
                    color: method.disabled ? '#94a3b8' : (paymentMethod === method.id ? '#6366f1' : '#374151'),
                    fontWeight: paymentMethod === method.id ? 600 : 400
                  }}>
                    {method.label}
                  </span>
                  {method.disabled && (
                    <span style={{
                      fontSize: 11, color: '#fff', backgroundColor: '#94a3b8',
                      padding: '2px 6px', borderRadius: 4, fontWeight: 600
                    }}>준비중</span>
                  )}
                  {!method.disabled && paymentMethod === method.id && <Check size={16} color="#6366f1" />}
                </button>
              ))}
            </div>

            {/* 무통장입금 안내 */}
            {paymentMethod === 'bank' && (
              <div style={{ marginTop: 16 }}>
                {/* 입금자명 입력 */}
                <div style={{
                  marginBottom: 16, padding: 16,
                  backgroundColor: '#fff', borderRadius: 12,
                  border: '1px solid #e2e8f0'
                }}>
                  <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                    입금자명 *
                  </label>
                  <input
                    type="text"
                    value={depositorName}
                    onChange={(e) => setDepositorName(e.target.value)}
                    placeholder="실제 입금하시는 분의 이름"
                    style={{
                      width: '100%', padding: '12px 16px',
                      border: '1px solid #e2e8f0', borderRadius: 10,
                      fontSize: 14, boxSizing: 'border-box'
                    }}
                  />
                </div>

                {/* 판매자별 계좌 정보 */}
                <div style={{
                  padding: 16,
                  backgroundColor: '#eff6ff', borderRadius: 12,
                  border: '1px solid #bfdbfe'
                }}>
                  <p style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 700, color: '#1e40af' }}>
                    입금 안내
                  </p>
                  {Object.keys(sellerBankInfoMap).length > 0 ? (
                    Object.entries(sellerBankInfoMap).map(([sellerId, info]) => (
                      <div key={sellerId} style={{
                        padding: 12, backgroundColor: '#fff', borderRadius: 8,
                        marginBottom: 8, border: '1px solid #dbeafe'
                      }}>
                        <p style={{ margin: '0 0 6px', fontSize: 14, fontWeight: 600, color: '#1e3a5f' }}>
                          {info.sellerName}
                        </p>
                        {info.bankName && info.bankAccount ? (
                          <>
                            <p style={{ margin: '0 0 2px', fontSize: 14, color: '#1e3a5f' }}>
                              {info.bankName} {info.bankAccount}
                            </p>
                            <p style={{ margin: 0, fontSize: 14, color: '#1e3a5f' }}>
                              예금주: {info.bankHolder}
                            </p>
                          </>
                        ) : (
                          <p style={{ margin: 0, fontSize: 13, color: '#f59e0b' }}>
                            판매자 계좌 정보가 아직 등록되지 않았습니다.
                          </p>
                        )}
                      </div>
                    ))
                  ) : (
                    <p style={{ margin: 0, fontSize: 14, color: '#64748b' }}>
                      판매자 계좌 정보를 불러오는 중...
                    </p>
                  )}
                  <p style={{ margin: '12px 0 0', fontSize: 13, color: '#64748b' }}>
                    주문 후 위 계좌로 입금하시면 판매자 확인 후 배송이 시작됩니다.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 오른쪽: 주문 요약 */}
        <div style={styles.rightPanel}>
          <div style={styles.summaryCard}>
            <h3 style={styles.summaryTitle}>주문 요약</h3>
            
            <div style={styles.summaryRow}>
              <span>상품 금액</span>
              <span>{subtotal.toLocaleString()}원</span>
            </div>
            <div style={styles.summaryRow}>
              <span>배송비</span>
              <span style={{ color: shippingFee === 0 ? '#10b981' : '#374151' }}>
                {shippingFee === 0 ? '무료' : `${shippingFee.toLocaleString()}원`}
              </span>
            </div>
            {shippingFee > 0 && subtotal > 0 && (
              <p style={styles.freeShippingHint}>
                {(50000 - subtotal).toLocaleString()}원 더 구매 시 무료배송
              </p>
            )}
            
            <div style={styles.divider} />
            
            <div style={styles.totalRow}>
              <span>총 결제금액</span>
              <span style={styles.totalAmount}>{total.toLocaleString()}원</span>
            </div>

            <label style={styles.agreeLabel}>
              <input
                type="checkbox"
                checked={agreeTerms}
                onChange={(e) => setAgreeTerms(e.target.checked)}
                style={styles.checkbox}
              />
              <span>주문 내용을 확인하였으며, 구매에 동의합니다.</span>
            </label>

            <button
              onClick={handlePayment}
              disabled={processing || cartItems.length === 0}
              style={{
                ...styles.payBtn,
                opacity: processing || cartItems.length === 0 ? 0.5 : 1,
                cursor: processing || cartItems.length === 0 ? 'not-allowed' : 'pointer'
              }}
            >
              {processing ? '처리 중...' : `${total.toLocaleString()}원 결제하기`}
            </button>

            <div style={styles.securityNote}>
              <AlertCircle size={14} color="#64748b" />
              <span>안전한 결제를 위해 SSL 암호화가 적용됩니다</span>
            </div>
          </div>
        </div>
      </div>

      {/* 저장된 배송지 모달 */}
      {showAddressModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>저장된 배송지</h3>
              <button onClick={() => setShowAddressModal(false)} style={styles.modalCloseBtn}>
                <X size={24} color="#64748b" />
              </button>
            </div>
            
            {savedAddresses.length === 0 ? (
              <div style={styles.modalEmpty}>
                <MapPin size={48} color="#cbd5e1" />
                <p style={{ marginTop: 16 }}>저장된 배송지가 없습니다</p>
              </div>
            ) : (
              <div style={styles.addressList}>
                {savedAddresses.map((addr, index) => (
                  <div key={addr.id} style={{
                    ...styles.addressCard,
                    backgroundColor: addr.isDefault ? '#eef2ff' : '#f8fafc',
                    border: addr.isDefault ? '2px solid #6366f1' : '1px solid #e2e8f0'
                  }}>
                    <div style={styles.addressCardHeader}>
                      <div style={styles.addressCardName}>
                        <span style={{ fontWeight: 700, fontSize: 16 }}>{addr.name}</span>
                        {addr.isDefault && (
                          <span style={styles.defaultBadge}>기본</span>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: 4 }}>
                        {!addr.isDefault && (
                          <button onClick={() => setDefaultAddress(index)} style={styles.starBtn}>
                            <Star size={12} />
                          </button>
                        )}
                        <button onClick={() => deleteAddress(index)} style={styles.deleteAddrBtn}>
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                    <p style={styles.addressCardPhone}>{addr.phone}</p>
                    <p style={styles.addressCardAddr}>[{addr.zipcode}] {addr.address} {addr.detailAddress}</p>
                    <button onClick={() => selectAddress(addr)} style={styles.selectAddrBtn}>
                      이 주소로 배송
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* 주소 검색 모달 */}
      {showPostcodeModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.postcodeModal}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>주소 검색</h3>
              <button onClick={() => setShowPostcodeModal(false)} style={styles.modalCloseBtn}>
                <X size={24} color="#64748b" />
              </button>
            </div>
            <div ref={postcodeRef} style={styles.postcodeContainer}></div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    minHeight: 'calc(100vh - 64px)',
    backgroundColor: '#f8fafc',
    padding: '24px'
  },
  content: {
    maxWidth: 1200,
    margin: '0 auto',
    display: 'flex',
    gap: 24,
    alignItems: 'flex-start'
  },
  leftPanel: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 20
  },
  rightPanel: {
    width: 360,
    position: 'sticky',
    top: 88
  },
  section: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 24,
    border: '1px solid #e2e8f0'
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 20,
    flexWrap: 'wrap'
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: '#0f172a',
    margin: 0
  },
  itemCount: {
    padding: '4px 10px',
    backgroundColor: '#eef2ff',
    color: '#6366f1',
    borderRadius: 12,
    fontSize: 13,
    fontWeight: 600
  },
  addressBtn: {
    padding: '8px 14px',
    backgroundColor: '#f1f5f9',
    color: '#475569',
    border: 'none',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 6
  },
  saveAddressBtn: {
    padding: '8px 14px',
    backgroundColor: '#6366f1',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 6
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px 20px',
    color: '#94a3b8'
  },
  shopBtn: {
    marginTop: 16,
    padding: '12px 24px',
    backgroundColor: '#6366f1',
    color: '#fff',
    border: 'none',
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer'
  },
  cartList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16
  },
  cartItem: {
    display: 'flex',
    gap: 16,
    padding: 16,
    backgroundColor: '#f8fafc',
    borderRadius: 12
  },
  itemImage: {
    width: 80,
    height: 80,
    objectFit: 'cover',
    borderRadius: 10,
    backgroundColor: '#e2e8f0'
  },
  itemInfo: {
    flex: 1,
    minWidth: 0
  },
  itemSeller: {
    fontSize: 12,
    color: '#64748b',
    margin: '0 0 4px 0'
  },
  itemName: {
    fontSize: 15,
    fontWeight: 600,
    color: '#0f172a',
    margin: '0 0 4px 0',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  itemOption: {
    fontSize: 13,
    color: '#64748b',
    margin: '0 0 8px 0'
  },
  itemPrice: {
    fontSize: 14,
    color: '#374151',
    margin: 0
  },
  itemActions: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 8
  },
  quantityControl: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: '4px 8px',
    border: '1px solid #e2e8f0'
  },
  qtyBtn: {
    width: 28,
    height: 28,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: '#64748b'
  },
  qtyValue: {
    fontSize: 14,
    fontWeight: 600,
    minWidth: 24,
    textAlign: 'center'
  },
  itemTotal: {
    fontSize: 16,
    fontWeight: 700,
    color: '#0f172a',
    margin: 0
  },
  deleteBtn: {
    padding: 8,
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: '#94a3b8'
  },
  formGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16
  },
  formRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6
  },
  label: {
    fontSize: 13,
    fontWeight: 600,
    color: '#374151'
  },
  input: {
    padding: '12px 16px',
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    fontSize: 14,
    outline: 'none',
    transition: 'border-color 0.2s',
    width: '100%',
    boxSizing: 'border-box'
  },
  select: {
    padding: '12px 16px',
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    fontSize: 14,
    outline: 'none',
    backgroundColor: '#fff',
    cursor: 'pointer'
  },
  addressRow: {
    display: 'flex',
    gap: 8
  },
  searchBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '12px 16px',
    backgroundColor: '#f1f5f9',
    border: 'none',
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
    color: '#374151',
    whiteSpace: 'nowrap'
  },
  paymentMethods: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 12
  },
  paymentBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '16px',
    border: '2px solid #e2e8f0',
    borderRadius: 12,
    backgroundColor: '#fff',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  summaryCard: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 24,
    border: '1px solid #e2e8f0'
  },
  summaryTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: '#0f172a',
    margin: '0 0 20px 0'
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
    fontSize: 14,
    color: '#64748b'
  },
  freeShippingHint: {
    fontSize: 12,
    color: '#f59e0b',
    margin: '0 0 16px 0',
    padding: '8px 12px',
    backgroundColor: '#fef3c7',
    borderRadius: 8
  },
  divider: {
    height: 1,
    backgroundColor: '#e2e8f0',
    margin: '16px 0'
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20
  },
  totalAmount: {
    fontSize: 24,
    fontWeight: 700,
    color: '#6366f1'
  },
  agreeLabel: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    fontSize: 13,
    color: '#64748b',
    marginBottom: 16,
    cursor: 'pointer'
  },
  checkbox: {
    width: 18,
    height: 18,
    marginTop: 2
  },
  payBtn: {
    width: '100%',
    padding: '16px',
    backgroundColor: '#6366f1',
    color: '#fff',
    border: 'none',
    borderRadius: 12,
    fontSize: 16,
    fontWeight: 700,
    cursor: 'pointer',
    marginBottom: 12
  },
  securityNote: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    fontSize: 12,
    color: '#94a3b8'
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '60vh',
    color: '#64748b'
  },
  spinner: {
    width: 40,
    height: 40,
    border: '3px solid #e2e8f0',
    borderTop: '3px solid #6366f1',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: 16
  },
  // 모달 스타일
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 24,
    width: '100%',
    maxWidth: 500,
    maxHeight: '80vh',
    overflow: 'auto'
  },
  postcodeModal: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 24,
    width: '100%',
    maxWidth: 500,
    height: '550px',
    display: 'flex',
    flexDirection: 'column'
  },
  postcodeContainer: {
    flex: 1,
    minHeight: '450px',
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    overflow: 'hidden'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20
  },
  modalTitle: {
    margin: 0,
    fontSize: 20,
    fontWeight: 700
  },
  modalCloseBtn: {
    padding: 8,
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer'
  },
  modalEmpty: {
    textAlign: 'center',
    padding: 40,
    color: '#94a3b8'
  },
  addressList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12
  },
  addressCard: {
    padding: 16,
    borderRadius: 12
  },
  addressCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8
  },
  addressCardName: {
    display: 'flex',
    alignItems: 'center',
    gap: 8
  },
  defaultBadge: {
    padding: '2px 8px',
    backgroundColor: '#6366f1',
    color: '#fff',
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 600
  },
  starBtn: {
    padding: '6px 10px',
    backgroundColor: '#fef3c7',
    color: '#b45309',
    border: 'none',
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer'
  },
  deleteAddrBtn: {
    padding: '6px 10px',
    backgroundColor: '#fee2e2',
    color: '#dc2626',
    border: 'none',
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer'
  },
  addressCardPhone: {
    margin: '0 0 4px 0',
    fontSize: 14,
    color: '#64748b'
  },
  addressCardAddr: {
    margin: 0,
    fontSize: 14,
    color: '#374151'
  },
  selectAddrBtn: {
    marginTop: 12,
    width: '100%',
    padding: '12px',
    backgroundColor: '#6366f1',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer'
  }
};

export default Cart;